import { useState, useEffect, useRef } from "react";
import { postData, getData } from "../../../../ApiServices/ApiService";
import {
    ORG_GET_INVOICE_LIST,
    ORG_UPDATE_BACK_DATE_APPROVAL_STATUS,
    ORG_CLIENT_SEARCH_TO_ADD_GROUPMEMBER,
    ORG_GET_SERVICE_LIST_SEARCH,
    ORG_GET_EMPLOYEE_LIST,
} from "../../../../ApiServices/BaseURL";

const ItemsPerPage = 10;

const BackDateApprovalController = (approvalList, onRefresh, error) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [searchUser, setSearchUser] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [clientList, setClientList] = useState([]);

    const [userRole, setUserRole] = useState('');

    const [openDropdown, setOpenDropdown] = useState(null); // Tracks the currently open dropdown
    const toggleDropdown = (dropdownName) => {
        setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
    };

    const dropdownRefs = useRef({}); // Ref for dropdown

    // Handle clicks outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutside = Object.keys(dropdownRefs.current).every(
                (key) => dropdownRefs.current[key] && !dropdownRefs.current[key].contains(event.target)
            );
            if (isOutside) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));
        setUserRole(storedUserInfo?.emp_role);
    }, [])

    useEffect(() => {
        setServiceListError(error);
    }, [error])

    const [filterData, setFilterData] = useState({
        clientName: null,
        services: null,
        generatedDate: "",
        generatedBy: null,
    });

    const [serviceOptions, setServiceOptions] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [servicesError, setServicesError] = useState("");

    const [recipientOptions, setRecipientOptions] = useState([]);
    const [userLoading, setUserLoading] = useState(false);
    const [userError, setUserError] = useState("");

    const [filterError, setFilterError] = useState('');
    const [serviceListError, setServiceListError] = useState('');
    const [serviceListLoading, setServiceListLoading] = useState(false);

    const [approvalInfoList, setApprovalInfoList] = useState([]);

    const totalPages = Math.ceil(approvalInfoList.length / ItemsPerPage);
    const indexOfLastItem = currentPage * ItemsPerPage;
    const indexOfFirstItem = indexOfLastItem - ItemsPerPage;
    const currentTasks = approvalInfoList.slice(indexOfFirstItem, indexOfLastItem);
    const maxVisiblePages = 3;
    const getVisiblePages = () => {
        const start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const end = Math.min(totalPages, start + maxVisiblePages - 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };
    const visiblePages = getVisiblePages();

    useEffect(() => {
        fetchServiceList();
        fetchUserList();
    }, []);

    useEffect(() => {
        setApprovalInfoList(approvalList);
    }, [approvalList]);

    const handlePageChange = (pageNum) => {
        if (pageNum > 0 && pageNum <= totalPages) {
            setCurrentPage(pageNum);
        }
    };

    const toggleFilters = () => setShowFilters(!showFilters);

    const applyFilters = () => {
        if (userRole === "Accountant") {
            setServiceListError('No data to display at the moment');
        }
        else {
            fetchInvoiceFilterList();
        }
    };

    const resetFilters = () => {
        if (filterData?.clientName || filterData?.services || filterData?.generatedDate || filterData?.generatedBy) {
            if (userRole !== "Accountant") {
                onRefresh();
            }
            setCurrentPage(1);
        }
        setFilterError('');
        setFilterData({
            clientName: null,
            services: null,
            generatedDate: "",
            generatedBy: null,
        });
        setClientList([]);
        setSearchUser('');
        setSearchError('');
        if (userRole === "Accountant") {
            setServiceListError('No data to display at the moment');
        }
        else {
            setServiceListError('');
        }
    };

    const cancelFilters = () => {
        resetFilters();
        setShowFilters(!showFilters);
    }

    const handleSearchInputText = (value) => {
        setSearchUser(value);
        setSearchError('');
        setClientList([]);
        if (!value) {
            setFilterData((prevData) => ({
                ...prevData,
                clientName: null,
            }));
        }
    };

    const handleInputChange = (field, value) => {
        setFilterData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    const handleSelectClient = (client) => {
        handleInputChange('clientName', client);
        setSearchUser(client?.client_name);
        setClientList([]);
    };

    const handleSearchClient = async () => {
        try {
            setSearchError('');
            setSearchLoading(true);
            setClientList([]);
            if (!searchUser) {
                setSearchError('Please enter valid text'); return;
            }
            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));
            const payLoad = { emp_id_auth: storedUserInfo?.emp_id, search_type: "GENERIC", search_word: searchUser }

            //console.log('team Data', payLoad);
            // Make the API call
            const response = await postData(ORG_CLIENT_SEARCH_TO_ADD_GROUPMEMBER, payLoad);
            const responseData = await response.json();
            //console.log('response Data', responseData);

            // Check for success (200) and internal status code
            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message.toLowerCase() === 'success') {
                        setClientList(responseData?.dataJ || []);
                    } else {
                        setSearchError(responseData?.info || 'Failed to fetch client. Please try again.');
                    }
                } else {
                    setSearchError(responseData?.info || 'Failed to fetch client. Status code error.');
                }
            }
            // Token-related errors: 400, 401, 403
            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                if (responseData?.message === 'Unauthorized' || responseData?.message === 'The incoming token has expired' || responseData?.message === 'Access Denied') {
                    setSearchError('Unauthorized access. Your session may have expired. Please log in again.');
                    // Optionally, redirect to the login page or refresh the token
                    // Example: redirectToLogin();
                } else {
                    setSearchError(responseData?.info || 'Bad request. Please check the request parameters.');
                }
            }
            // Internal Server Error: 500
            else if (response?.status === 500) {
                setSearchError(responseData?.info || 'Internal Server Error. Please try again later.');
            }
            // Unexpected or network-related errors
            else {
                setSearchError('Unexpected error occurred. Please try again later.');
            }
        } catch (error) {
            // Handle various error scenarios
            if (error.response) {
                // Server responded with a status outside the 2xx range
                setSearchError(`Error: ${error.response.status}. ${error.response.data?.message || 'Failed to fetch client. Please try again.'}`);
            } else if (error.request) {
                // Request was made but no response was received
                setSearchError('No response received from the server. Please check your network connection and try again.');
            } else {
                // An error occurred in setting up the request
                setSearchError(`Failed to fetch client. Please check your network connection and try again.`);
            }
        } finally {
            setSearchLoading(false);
        }
    };

    const fetchServiceList = async () => {
        setServicesLoading(true);
        setServiceOptions([]);
        setServicesError('');
        try {
            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_')); // Get user info from localStorage
            const payload = {
                emp_id_auth: storedUserInfo?.emp_id,
            };

            // API call to fetch GST services
            const response = await postData(ORG_GET_SERVICE_LIST_SEARCH, payload);
            const responseData = await response.json();

            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message?.toLowerCase() === "success") {
                        const services = responseData?.dataJ.map((service) => ({
                            label: service.service_name,
                            value: service.service_id,
                        }));
                        setServiceOptions(services);
                    } else {
                        setServicesError(responseData?.info || 'Service search failed.');
                    }
                } else {
                    setServicesError(responseData?.info || 'Unexpected response code from the server.');
                }
            }
            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                if (responseData?.message === 'Unauthorized' || responseData?.message === 'The incoming token has expired' || responseData?.message === 'Access Denied') {
                    setServicesError('Unauthorized access. Your session may have expired. Please log in again.');
                    // Optionally, redirect to the login page or refresh the token
                    // Example: redirectToLogin();
                } else {
                    setServicesError(responseData?.info || 'Bad request. Please check the request parameters.');
                }
            }
            // Internal Server Error: 500
            else if (response?.status === 500) {
                setServicesError(responseData?.info || 'Internal Server Error. Please try again later.');
            }
            // Unexpected or network-related errors
            else {
                setServicesError('Unexpected error occurred. Please try again later.');
            }
        } catch (error) {
            // Handle various error scenarios
            if (error.response) {
                // Server responded with a status outside the 2xx range
                setServicesError(`Error: ${error.response.status}. ${error.response.data?.message || 'Failed to get service options. Please try again.'}`);
            } else if (error.request) {
                // Request was made but no response was received
                setServicesError('No response received from the server. Please check your network connection and try again.');
            } else {
                // An error occurred in setting up the request
                setServicesError(`Failed to search service options. Please check your network connection and try again.`);
            }
        } finally {
            setServicesLoading(false);
        }
    };
    const fetchUserList = async () => {
        try {
            setUserLoading(true);
            setUserError('');

            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));

            // Make the API call
            const response = await getData(`${ORG_GET_EMPLOYEE_LIST}?emp_id_auth=${storedUserInfo?.emp_id}`);
            const responseData = await response.json();

            // Check for success (200) and internal status code
            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message.toLowerCase() === 'success') {
                        if (responseData?.dataJ?.length > 0) {
                            const formattedTeams = responseData.dataJ.map(service => ({
                                label: service.emp_name,
                                value: service.emp_id
                            }));
                            setRecipientOptions(formattedTeams);
                        } else {
                            setUserError(responseData?.info || 'Data Not Found');
                        }
                    } else {
                        setUserError(responseData?.info || 'Failed to fetch User data. Please try again.');
                    }
                } else {
                    setUserError(responseData?.info || 'Failed to fetch User data. Status code error.');
                }
            }
            // Token-related errors: 400, 401, 403
            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                if (responseData?.message === 'Unauthorized' || responseData?.message === 'The incoming token has expired' || responseData?.message === 'Access Denied') {
                    setUserError('Unauthorized access. Your session may have expired. Please log in again.');
                    // Optionally, redirect to the login page or refresh the token
                    // Example: redirectToLogin();
                } else {
                    setUserError(responseData?.info || 'Bad request. Please check the request parameters.');
                }
            }
            // Internal Server Error: 500
            else if (response?.status === 500) {
                setUserError(responseData?.info || 'Internal Server Error. Please try again later.');
            }
            // Unexpected or network-related errors
            else {
                setUserError('Unexpected error occurred. Please try again later.');
            }
        } catch (error) {
            // Handle various error scenarios
            if (error.response) {
                // Server responded with a status outside the 2xx range
                setUserError(`Error: ${error.response.status}. ${error.response.data?.message || 'Failed to fetch User data. Please try again.'}`);
            } else if (error.request) {
                // Request was made but no response was received
                setUserError('No response received from the server. Please check your network connection and try again.');
            } else {
                // An error occurred in setting up the request
                setUserError(`Failed to fetch User data. Please check your network connection and try again.`);
            }
        } finally {
            setUserLoading(false);
        }
    }

    const fetchInvoiceFilterList = async () => {
        try {
            setFilterError('');
            if (!(filterData?.clientName || filterData?.services || filterData?.generatedDate || filterData?.generatedBy)) {
                setFilterError('Please enter/select any one of the above');
                return;
            }
            setServiceListLoading(true);
            setServiceListError('');

            setApprovalInfoList([]);
            setCurrentPage(1);
            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));

            const payLoad = {
                "emp_id_auth": storedUserInfo?.emp_id,
                "client_id": filterData?.clientName?.client_id,
                "service_id": filterData?.services?.value,
                "invoice_date": filterData?.generatedDate,
                "invoice_gen_emp_id": filterData?.generatedBy?.value,
                "query_type": "filter",
                "group_id": 0,
                "list_type": "approval"
            }

            console.log('---invoice filter:---', payLoad);

            const response = await postData(ORG_GET_INVOICE_LIST, payLoad);
            const responseData = await response.json();
            //console.log('---team responseData:---',responseData);

            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message.toLowerCase() === 'success') {
                        if (responseData?.dataJ?.length > 0) {
                            // teamListRef.current = responseData?.dataJ;
                            setApprovalInfoList(responseData?.dataJ);
                        } else {
                            setServiceListError(responseData?.info || 'No invoice list available');
                        }
                    } else {
                        setServiceListError(responseData?.info || 'Failed to fetch invoice list. Please try again.');
                    }
                } else {
                    setServiceListError(responseData?.info || 'Failed to fetch invoice list. Status code error.');
                }
            }
            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                if (responseData?.message === 'Unauthorized' || responseData?.message === 'The incoming token has expired' || responseData?.message === 'Access Denied') {
                    setServiceListError('Unauthorized access. Your session may have expired. Please log in again.');
                } else {
                    setServiceListError(responseData?.info || 'Bad request. Please check the request parameters.');
                }
            }
            else if (response?.status === 500) {
                setServiceListError(responseData?.info || 'Internal Server Error. Please try again later.');
            }
            else {
                setServiceListError('Unexpected error occurred. Please try again later.');
            }
        } catch (error) {
            if (error.response) {
                setServiceListError(`Error: ${error.response.status}. ${error.response.data?.message || 'Failed to fetch invoice list. Please try again.'}`);
            } else if (error.request) {
                setServiceListError('No response received from the server. Please check your network connection and try again.');
            } else {
                setServiceListError(`Failed to fetch invoice list. Please check your network connection and try again.`);
            }
        } finally {
            setServiceListLoading(false);
        }
    }

    const updateStatus = async (invoiceId, status) => {
        setActionError('');
        setActionSuccess('');
        try {
            setUpdatingStatus(prev => ({ ...prev, [invoiceId]: status === 2 ? 'approve' : 'reject' }));

            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));
            const payload = {
                emp_id_auth: storedUserInfo.emp_id,
                invoice_id: invoiceId,
                status: status
            };

            const response = await postData(ORG_UPDATE_BACK_DATE_APPROVAL_STATUS, payload);
            const data = await response.json();

            if (response?.status === 200) {
                if (data?.statusCode === 200) {
                    if (data?.message?.toLowerCase() === 'success') {
                        setActionSuccess((prev) => ({ ...prev, [invoiceId]: data?.info || '' }));
                        setTimeout(() => {
                            onRefresh();
                            setActionSuccess((prev) => ({ ...prev, [invoiceId]: '' }))
                        }, 3000);
                    } else {
                        setActionError((prev) => ({ ...prev, [invoiceId]: data?.info || 'Failed to update status. Please try again.' }));
                    }
                } else {
                    setActionError((prev) => ({ ...prev, [invoiceId]: data?.info || 'Failed to update status. Status code error.' }));
                }
            }

            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                if (data?.message === 'Unauthorized' || data?.message === 'The incoming token has expired' || data?.message === 'Access Denied') {
                    setActionError((prev) => ({ ...prev, [invoiceId]: 'Unauthorized access. Your session may have expired. Please log in again.' }));
                } else {
                    setActionError((prev) => ({ ...prev, [invoiceId]: data?.info || 'Bad request. Please check the request parameters.' }));
                }
            }

            else if (response?.status === 500) {
                setActionError((prev) => ({ ...prev, [invoiceId]: data?.info || 'Internal Server Error. Please try again later.' }));
            }

            else {
                setActionError((prev) => ({ ...prev, [invoiceId]: 'Unexpected error occurred. Please try again later.' }));
            }
        } catch (error) {
            if (error.response) {
                setActionError((prev) => ({ ...prev, [invoiceId]: `Error: ${error.response.status}. ${error.response.data?.message || 'Failed to update status. Please try again.'}` }));
            } else if (error.request) {
                setActionError((prev) => ({ ...prev, [invoiceId]: 'No response received from the server. Please check your network connection and try again.' }));
            } else {
                setActionError((prev) => ({ ...prev, [invoiceId]: 'Failed to update status. Please check your network connection and try again.' }));
            }
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [invoiceId]: null }));
        }
    };

    const handleApprove = (item) => {
        const statusCode = item?.back_date_approval === 21 ? 22 : 2;
        updateStatus(item.invoice_id, statusCode);
    };

    const handleReject = (item) => {
        const statusCode = item?.back_date_approval === 21 ? 23 : 3;
        updateStatus(item.invoice_id, statusCode);
    };


    return {
        currentTasks,
        currentPage,
        totalPages,
        visiblePages: getVisiblePages(),
        handlePageChange,
        handleApprove,
        handleReject,
        updatingStatus,
        showFilters,
        toggleFilters,
        searchUser,
        handleSearchInputText,
        handleSearchClient,
        searchLoading,
        searchError,
        clientList,
        handleSelectClient,
        filterData,
        handleInputChange,
        openDropdown,
        toggleDropdown,
        dropdownRefs,
        serviceOptions,
        servicesLoading,
        servicesError,
        recipientOptions,
        userLoading,
        userError,
        applyFilters,
        cancelFilters,
        resetFilters,
        filterError,
        serviceListLoading,
        serviceListError,
        actionError,
        actionSuccess,
    };
};

export default BackDateApprovalController;
